import { useState, useEffect } from 'react';
import { adminGetDiscounts, adminCreateDiscount } from '../../lib/adminApi';
import AdminSidebar from '../../components/AdminSidebar';

const NAVY = '#0a2540'; const ORANGE = '#F37428'; const BLUE = '#0176CE';

function fmtDate(raw) { if (!raw) return '—'; return new Date(raw).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }

function StatusBadge({ active }) {
  return <span style={{ background: active ? 'rgba(5,150,105,0.08)' : 'rgba(100,116,139,0.08)', color: active ? '#059669' : '#64748b', borderRadius: 5, fontSize: 10, fontWeight: 600, padding: '2px 8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{active ? 'active' : 'inactive'}</span>;
}

const EMPTY = { code: '', discount_type: 'percent', discount_value: '', max_uses: '', expires_on: '', notes: '' };

export default function AdminDiscounts() {
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [showForm, setShowForm]   = useState(false);
  const [form, setForm]           = useState(EMPTY);
  const [saving, setSaving]       = useState(false);
  const [saveError, setSaveError] = useState('');

  function load() {
    setLoading(true);
    adminGetDiscounts()
      .then(res => { setDiscounts(res.discounts || res || []); setLoading(false); })
      .catch(e  => { setError(e.message); setLoading(false); });
  }

  useEffect(load, []);

  async function handleCreate(e) {
    e.preventDefault();
    setSaveError(''); setSaving(true);
    try {
      const payload = {
        code: form.code.trim().toUpperCase(),
        discount_type: form.discount_type,
        discount_value: parseFloat(form.discount_value),
        max_uses: form.max_uses ? parseInt(form.max_uses) : null,
        expires_on: form.expires_on || null,
        notes: form.notes || null,
      };
      await adminCreateDiscount(payload);
      setShowForm(false);
      setForm(EMPTY);
      load();
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={s.page}>
      <AdminSidebar active="discounts" />
      <main style={s.main}>
        <div style={s.header}>
          <div>
            <h1 style={s.title}>Discount Codes</h1>
            <p style={s.sub}>{discounts.length} codes</p>
          </div>
          <button style={s.addBtn} onClick={() => { setShowForm(true); setForm(EMPTY); setSaveError(''); }}>+ Create Code</button>
        </div>

        {loading && <div style={{ color: '#64748b', padding: 32 }}>Loading…</div>}
        {error   && <div style={{ color: '#ef4444', padding: 32 }}>{error}</div>}

        {!loading && !error && (
          <div style={s.card}>
            <table style={s.table}>
              <thead>
                <tr>{['Code', 'Type', 'Value', 'Uses', 'Max Uses', 'Expires', 'Status'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {discounts.map(d => (
                  <tr key={d.discount_id || d.code} style={s.tr}>
                    <td style={{ ...s.td, fontWeight: 700, color: NAVY, fontFamily: 'monospace', letterSpacing: '0.05em' }}>{d.code}</td>
                    <td style={{ ...s.td, textTransform: 'capitalize', color: '#64748b' }}>{d.discount_type}</td>
                    <td style={{ ...s.td, fontWeight: 600, color: BLUE }}>
                      {d.discount_type === 'percent' ? `${d.discount_value}%` : `$${d.discount_value}`}
                    </td>
                    <td style={{ ...s.td, textAlign: 'center' }}>{d.times_used ?? 0}</td>
                    <td style={{ ...s.td, textAlign: 'center', color: '#94a3b8' }}>{d.max_uses ?? '∞'}</td>
                    <td style={{ ...s.td, color: '#94a3b8', fontSize: 12 }}>{fmtDate(d.expires_on)}</td>
                    <td style={s.td}><StatusBadge active={d.is_active !== false && d.active !== false} /></td>
                  </tr>
                ))}
                {discounts.length === 0 && (
                  <tr><td colSpan={7} style={{ ...s.td, textAlign: 'center', color: '#94a3b8', padding: 32 }}>No discount codes yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {showForm && (
        <div style={s.overlay}>
          <div style={s.modal}>
            <h2 style={s.modalTitle}>Create Discount Code</h2>
            {saveError && <div style={s.errorBox}>{saveError}</div>}
            <form onSubmit={handleCreate} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={s.label}>Code *</label>
                <input style={s.input} placeholder="SAVE20" value={form.code}
                  onChange={e => setForm(f => ({ ...f, code: e.target.value }))} required />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={s.label}>Type *</label>
                  <select style={s.input} value={form.discount_type} onChange={e => setForm(f => ({ ...f, discount_type: e.target.value }))}>
                    <option value="percent">Percent (%)</option>
                    <option value="fixed">Fixed ($)</option>
                  </select>
                </div>
                <div>
                  <label style={s.label}>Value *</label>
                  <input style={s.input} type="number" min="0" step="0.01" placeholder={form.discount_type === 'percent' ? '20' : '10.00'}
                    value={form.discount_value} onChange={e => setForm(f => ({ ...f, discount_value: e.target.value }))} required />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={s.label}>Max Uses (blank = unlimited)</label>
                  <input style={s.input} type="number" min="1" placeholder="100"
                    value={form.max_uses} onChange={e => setForm(f => ({ ...f, max_uses: e.target.value }))} />
                </div>
                <div>
                  <label style={s.label}>Expires On (optional)</label>
                  <input style={s.input} type="date" value={form.expires_on}
                    onChange={e => setForm(f => ({ ...f, expires_on: e.target.value }))} />
                </div>
              </div>
              <div>
                <label style={s.label}>Notes (optional)</label>
                <input style={s.input} placeholder="Internal note…" value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                <button type="submit" style={s.btnPrimary} disabled={saving}>{saving ? 'Creating…' : 'Create Code'}</button>
                <button type="button" style={s.btnGhost} onClick={() => setShowForm(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  page:      { display: 'flex', minHeight: '100vh', fontFamily: "'Inter', system-ui, sans-serif" },
  main:      { flex: 1, background: '#f0f5fb', padding: '36px 40px', overflowY: 'auto' },
  header:    { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28 },
  title:     { color: NAVY, fontSize: 26, fontWeight: 700, margin: 0, letterSpacing: '-0.4px' },
  sub:       { color: '#64748b', fontSize: 13, margin: '4px 0 0' },
  addBtn:    { background: ORANGE, border: 'none', borderRadius: 8, color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, padding: '10px 18px' },
  card:      { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' },
  table:     { width: '100%', borderCollapse: 'collapse' },
  th:        { color: '#94a3b8', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '10px 16px', textAlign: 'left', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' },
  tr:        { borderBottom: '1px solid #f1f5f9' },
  td:        { color: '#374151', fontSize: 13, padding: '12px 16px', verticalAlign: 'middle' },
  overlay:   { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal:     { background: '#fff', borderRadius: 16, padding: '36px 32px', width: '100%', maxWidth: 480, boxShadow: '0 24px 60px rgba(0,0,0,0.25)', maxHeight: '90vh', overflowY: 'auto' },
  modalTitle:{ color: NAVY, fontSize: 18, fontWeight: 700, margin: '0 0 20px' },
  label:     { display: 'block', color: '#64748b', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 5 },
  input:     { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, color: NAVY, fontSize: 14, padding: '10px 12px', outline: 'none', width: '100%', boxSizing: 'border-box' },
  errorBox:  { background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, color: '#dc2626', fontSize: 13, padding: '10px 14px', marginBottom: 16 },
  btnPrimary:{ background: ORANGE, border: 'none', borderRadius: 8, color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600, padding: '11px 20px' },
  btnGhost:  { background: 'transparent', border: '1px solid #e2e8f0', borderRadius: 8, color: '#64748b', cursor: 'pointer', fontSize: 14, fontWeight: 500, padding: '11px 20px' },
};
